'use client'

import Link from 'next/link'
import { useEffect, useRef, type FC } from 'react'
import { Corridor } from '../atoms/Corridor'
import type { IconCircleVariants } from '../atoms/IconCircle'
import { Logo } from '../atoms/Logo'
import { NavButton } from '../atoms/NavButton'
import type { NavState } from './NavOverlay'

type HeaderProps = {
	theme?: IconCircleVariants['theme']
	closeOnEsc?: boolean
	state: NavState
	onNavigate: (state: NavState) => void
}

export const Header: FC<HeaderProps> = ({
	theme = 'white',
	closeOnEsc = false,
	state,
	onNavigate,
}) => {
	// Handle close on ESC key if behaviour is 'close-on-esc'
	const ref = useRef<HTMLElement>(null)
	useEffect(() => {
		const header = ref.current
		if (header && closeOnEsc && state !== 'closed') {
			const handleKeyDown = (event: KeyboardEvent) => {
				if (event.key === 'Escape') {
					header.querySelector<HTMLButtonElement>('button[aria-expanded="true"]')?.focus()
					onNavigate('closed')
				}
			}
			window.addEventListener('keydown', handleKeyDown)
			return () => window.removeEventListener('keydown', handleKeyDown)
		}
	}, [closeOnEsc, state, onNavigate])

	// Divert menu button focus to the fixed menu button
	const refFixedMenu = useRef<HTMLButtonElement>(null)
	const refSearchButton = useRef<HTMLButtonElement>(null)

	return (
		<header
			ref={ref}
			data-theme={theme}
			className={theme === 'white' ? 'text-blue bg-white' : 'text-white bg-blue'}
		>
			{/* Header content */}
			<Corridor className="h-header-height flex items-center justify-between py-xl tablet-l:py-2xl">
				<Link href="/" aria-label="GIICA" target="_self" className="relative block z-45">
					<Logo className="h-auto w-47.5 tablet-l:w-54 transition-colors duration-200" />
				</Link>
				{/* Add space on the right hand side to account for sticky menu button */}
				<nav className="flex items-center gap-x-xl pr-11">
					<NavButton
						ref={refSearchButton}
						state="search"
						label="Search"
						icon={['fas', 'search']}
						intent="secondary"
						theme={theme}
						active={state === 'search'}
						onClick={() => onNavigate('search')}
					/>
					<NavButton
						state="menu"
						label="Menu"
						intent="primary"
						theme={theme}
						active={state === 'menu'}
						className="tablet-l:pr-md"
						onClick={() => onNavigate('menu')}
						onFocus={() => refFixedMenu.current?.focus()}
					/>
				</nav>
			</Corridor>
			{/* Sticky menu button */}
			<nav className="fixed top-0 right-0 w-full z-90 h-header-height pointer-events-none">
				<Corridor className="h-full flex items-center justify-end">
					<NavButton
						ref={refFixedMenu}
						state="menu"
						aria-label="Menu"
						icon={['fas', 'bars']}
						intent="primary"
						theme={theme}
						active={state === 'menu'}
						onClick={() => onNavigate('menu')}
						onKeyDown={(event) => {
							if (event.key === 'Tab' && event.shiftKey) {
								event.preventDefault()
								refSearchButton.current?.focus()
							}
						}}
					/>
				</Corridor>
			</nav>
		</header>
	)
}
